import React, { useState } from 'react';
import db from '../lib/instant';
import TaskDetailModal from './TaskDetailModal';

const TaskCard = ({ task, users = [], currentUser, onTaskUpdated }) => {
  const user = currentUser || db.useUser();
  const [loading, setLoading] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const canComplete = () => {
    return task.created_by === user?.id || task.assigned_to === user?.id;
  };

  const handleCompletionToggle = async () => {
    if (loading) return;
    
    setLoading(true);
    try {
      const isCompleted = task.status === 'completed';
      const updateData = {
        status: isCompleted ? 'pending' : 'completed',
        updated_at: new Date().toISOString()
      };

      if (!isCompleted) {
        updateData.completed_at = new Date().toISOString();
      } else {
        updateData.completed_at = null;
      }

      await db.transact([
        db.tx.tasks[task.id].update(updateData)
      ]);

      onTaskUpdated?.();
    } catch (err) {
      console.error('Error updating task completion:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!canComplete() || !window.confirm('Are you sure you want to delete this task?')) {
      return;
    }

    setLoading(true);
    try {
      await db.transact([
        db.tx.tasks[task.id].delete()
      ]);
      onTaskUpdated?.();
    } catch (err) {
      console.error('Error deleting task:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString();
  };

  const isOverdue = () => {
    if (!task.due_date || task.status === 'completed') return false;
    return new Date(task.due_date) < new Date();
  };

  const isCompleted = () => {
    return task.status === 'completed';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCardClasses = () => {
    let classes = "task-card bg-white rounded-xl border p-5 cursor-pointer transition-all duration-300 hover-lift ";
    
    if (isCompleted()) {
      classes += "opacity-75 bg-green-50 border-green-200 ";
    } else {
      classes += "border-gray-200 ";
    }
    
    if (isOverdue()) {
      classes += "border-red-300 bg-red-50 ";
    }
    
    if (task.is_timer_running === 'true') {
      classes += "timer-active ";
    }
    
    return classes;
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    } else {
      return `${seconds}s`;
    }
  };

  const getTimeSpent = () => {
    const baseTime = parseInt(task.time_spent || '0');
    if (task.is_timer_running === 'true' && task.timer_started_at) {
      const sessionTime = Math.floor((Date.now() - new Date(task.timer_started_at).getTime()) / 1000);
      return baseTime + sessionTime;
    }
    return baseTime;
  };


  return (
    <>
      <div 
        className={getCardClasses()}
        onClick={() => setShowDetailModal(true)}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          {/* Checkbox */}
          <div className="flex items-start pt-1 mr-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCompletionToggle();
              }}
              disabled={!canComplete() || loading}
              className="relative w-5 h-5 rounded border-2 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: isCompleted() ? '#10b981' : 'transparent',
                borderColor: isCompleted() ? '#10b981' : '#d1d5db'
              }}
            >
              {isCompleted() && (
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                </svg>
              )}
            </button>
          </div>

          <div className="flex-1 pr-4">
            <h3 className={`font-semibold text-lg mb-1 leading-tight transition-all ${
              isCompleted() ? 'text-gray-500 line-through' : 'text-gray-900'
            }`}>
              {task.title}
            </h3>
            {task.description && (
              <p className={`text-sm line-clamp-2 leading-relaxed transition-all ${
                isCompleted() ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {task.description}
              </p>
            )}
          </div>
          
          <div className="flex flex-col items-end space-y-2">
            <div className="flex items-center space-x-2">
              <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getPriorityColor(task.priority)}`}>
                {task.priority}
              </span>
              {isCompleted() && (
                <span className="px-3 py-1 text-xs font-medium rounded-full border bg-green-100 text-green-800">
                  completed
                </span>
              )}
            </div>
            
            {/* Time tracking display */}
            {getTimeSpent() > 0 && (
              <div className={`flex items-center text-xs px-2 py-1 rounded-full ${
                task.is_timer_running === 'true' 
                  ? 'bg-blue-100 text-blue-800 animate-pulse' 
                  : 'bg-gray-100 text-gray-600'
              }`}>
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                {formatTime(getTimeSpent())}
                {task.is_timer_running === 'true' && <span className="ml-1">⏸</span>}
              </div>
            )}
          </div>
        </div>

        {/* Tags */}
        {task.tags && task.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4">
            {task.tags.slice(0, 3).map((tag, index) => (
              <span key={index} className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded-md border border-blue-200">
                {tag}
              </span>
            ))}
            {task.tags.length > 3 && (
              <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-md">
                +{task.tags.length - 3} more
              </span>
            )}
          </div>
        )}

        {/* Meta info */}
        <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
          <div className="flex items-center space-x-4">
            {task.assigned_to && (
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
                <span>
                  {users.find(u => u.id === task.assigned_to)?.email?.split('@')[0] || 
                   (task.assigned_to === user?.id ? 'You' : 'Unknown')}
                </span>
              </div>
            )}
            
            {task.project_id === null && (
              <div className="flex items-center text-blue-600">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
                Personal
              </div>
            )}
            
            {task.due_date && (
              <div className={`flex items-center ${isOverdue() ? 'text-red-600 font-medium' : ''}`}>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                </svg>
                {formatDate(task.due_date)}
                {isOverdue() && <span className="ml-1">⚠️</span>}
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        {canComplete() && (
          <div className="flex items-center justify-end pt-4 border-t border-gray-100">
            <button 
              onClick={(e) => { e.stopPropagation(); setShowDetailModal(true); }}
              className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center"
            >
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
              </svg>
              Details
            </button>
          </div>
        )}
        
        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center rounded-xl">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full spinner"></div>
          </div>
        )}
      </div>

      {/* Task Detail Modal */}
      <TaskDetailModal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        task={task}
        users={users}
        currentUser={user}
        onTaskUpdated={onTaskUpdated}
      />
    </>
  );
};

export default TaskCard;